import { ApiError } from './api';
import type { ProblemDetails } from '../types/workspace';
import type { CorrectionRequestOptions, SegmentCorrectionPreview } from '../types/correction';
import type { TiptapDoc } from '../types/entities';

export async function streamCorrectionPreview(versionId: string, baseContentDoc: TiptapDoc, options: CorrectionRequestOptions): Promise<SegmentCorrectionPreview> {
  const response = await fetch(`/api/v1/scene-segment-versions/${versionId}/correction/preview`, {
    method: 'POST', cache: 'no-store', signal: options.signal, headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ baseContentDoc, reasoningEffort: options.reasoningEffort ?? 'low', senfLevel: options.senfLevel, stream: true })
  });
  if (!response.ok) {
    let problem: ProblemDetails;
    try { problem = await response.json() as ProblemDetails; } catch { problem = { title: `HTTP ${response.status}`, code: 'HTTP_ERROR', status: response.status }; }
    throw new ApiError(problem);
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Die Korrekturverbindung liefert keine Antwort.');
  const decoder = new TextDecoder(); let pending = ''; let result: SegmentCorrectionPreview | undefined;
  function frame(value: string) {
    const lines = value.split('\n');
    const event = lines.find((line) => line.startsWith('event:'))?.slice(6).trim();
    const serialized = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trimStart()).join('\n');
    if (!serialized) return;
    const data = JSON.parse(serialized);
    if (event === 'progress') options.onProgress?.(data);
    if (event === 'result') result = data as SegmentCorrectionPreview;
    if (event === 'error') throw new ApiError(data as ProblemDetails);
  }
  try {
    while (true) {
      const { value, done } = await reader.read();
      pending += done ? decoder.decode() : decoder.decode(value, { stream: true });
      pending = pending.replace(/\r\n/g, '\n');
      let boundary: number;
      while ((boundary = pending.indexOf('\n\n')) >= 0) { frame(pending.slice(0, boundary)); pending = pending.slice(boundary + 2); }
      if (pending.length > 10_000_000) throw new Error('Die Korrekturantwort überschreitet das Größenlimit.');
      if (done) break;
    }
    if (pending.trim()) frame(pending);
    if (!result) throw new Error('Die Korrekturverbindung endete vor der vollständigen Vorschau.');
    return result;
  } finally { reader.releaseLock(); }
}
